import { useState, useEffect, useMemo } from 'react'
import { Box, Flex, Input, Text, VStack } from '@chakra-ui/react'
import { phoneNumbers } from '../../countries/PhoneDial'
import ModalSelect from './modalSelect'

interface ModalPhoneInputProps {
  label?: string
  value: string
  onChange: (fullPhone: string) => void
  required?: boolean
  isInvalid?: boolean
  error?: string
  defaultCountry?: string
}

const ModalPhoneInput: React.FC<ModalPhoneInputProps> = ({
  label,
  value,
  onChange,
  required,
  isInvalid,
  error,
  defaultCountry = 'nl',
}) => {
  const [country, setCountry] = useState(defaultCountry.toLowerCase())
  const [dialCode, setDialCode] = useState(() => {
    const found = phoneNumbers.find((c) => c.code.toLowerCase() === defaultCountry.toLowerCase())
    return found?.dial_code ?? '+1'
  })
  const [localNumber, setLocalNumber] = useState('')

  const countryOptions = useMemo(
    () =>
      phoneNumbers.map((c) => ({
        value: c.code.toLowerCase(),
        label: `${c.name} (${c.dial_code})`,
        countryCode: c.code.toLowerCase(),
      })),
    [],
  )

  useEffect(() => {
    if (!value) {
      setLocalNumber('')
      return
    }
    const matched = phoneNumbers.find((c) => value.startsWith(c.dial_code))
    if (matched) {
      setCountry(matched.code.toLowerCase())
      setDialCode(matched.dial_code)
      setLocalNumber(value.slice(matched.dial_code.length))
    } else {
      setLocalNumber(value)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleCountryChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const code = e.target.value
    setCountry(code)
    const found = phoneNumbers.find((c) => c.code.toLowerCase() === code.toLowerCase())
    if (found) {
      setDialCode(found.dial_code)
      setLocalNumber('')
      onChange(found.dial_code)
    }
  }

  const handleNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const num = e.target.value
    setLocalNumber(num)
    onChange(`${dialCode}${num}`)
  }

  const placeholder = phoneNumbers.find(
    (c) => c.code.toLowerCase() === country.toLowerCase()
  )?.example?.replace(/^(\+\d+\s?)/, '') || ''

  return (
    <VStack align="left" spacing="4px" width="100%">
      {label && (
        <Text textStyle="l-xs" color={required ? 'solid.700' : 'solid.500'} fontWeight={required ? '600' : undefined}>
          {label}
          {required && <Text as="span" color="primary.100"> *</Text>}
        </Text>
      )}
      <Flex borderRadius="8px" alignItems="stretch">
        {/* Country selector */}
        <Box
          flexShrink={0}
          w="auto"
          sx={{
            '& > div': { width: 'auto' },
            '& select': {
              borderRadius: '8px 0 0 8px !important',
              borderRight: 'none !important',
              minH: '40px',
              pr: '36px !important',
              pl: '44px !important',
            },
          }}
        >
          <ModalSelect
            name="phone-country"
            value={country}
            options={countryOptions}
            onChange={handleCountryChange}
            showCountryFlag
            searchDisabled={false}
            compact
            hideClear
          />
        </Box>

        {/* Phone number input */}
        <Flex align="center" flex="1" position="relative">
          <Text
            textStyle="l-s"
            color="solid.500"
            position="absolute"
            left="12px"
            zIndex={1}
            userSelect="none"
            pointerEvents="none"
          >
            {dialCode}
          </Text>
          <Input
            flex="1"
            borderRadius="0 8px 8px 0"
            pl={`${20 + dialCode.length * 8}px`}
            value={localNumber}
            onChange={handleNumberChange}
            placeholder={placeholder}
            isInvalid={isInvalid}
            bg={required ? 'primary.0' : undefined}
            borderColor={required ? 'primary.50' : undefined}
            _focus={required ? { borderColor: 'primary.100', bg: 'white' } : undefined}
          />
        </Flex>
      </Flex>

      {error && (
        <Text textStyle="b-xs" color="error.100">
          {error}
        </Text>
      )}
    </VStack>
  )
}

export default ModalPhoneInput
